import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

/**
 * Create an advertiser account (round 8, decision A1).
 *
 * Lifted out of pages/AdminAccounts.jsx, which was ROUTED BUT NOT IN THE NAV - reachable
 * only by typing the URL, and the only place in the product where an advertiser account
 * could be created. So the capability was real and the page holding it was invisible.
 *
 * A component rather than a paste, because the Advertisers page is already 22KB and this
 * is a self-contained job with its own state. It also means AdminAccounts can be deleted
 * without the form going with it.
 *
 * `onCreated` fires after a successful create so the owner can refresh its own list; the
 * dialog does not know or care what that list is.
 */
const TIERS = ["BRONZE", "SILVER", "GOLD"];

const EMPTY = {
  business_name: "",
  abn: "",
  tier: "BRONZE",
  primary_contact: { name: "", email: "", phone: "", role: "Marketing", is_emergency_contact: false },
};

export default function CreateAdvertiserDialog({ open, onOpenChange, onCreated }) {
  const { token } = useAuth();
  const [formData, setFormData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const setContact = (field, value) =>
    setFormData((prev) => ({
      ...prev,
      primary_contact: { ...prev.primary_contact, [field]: value },
    }));

  const close = (isOpen) => {
    // Reset on close so a cancelled create does not prefill the next one with a
    // half-typed business.
    if (!isOpen) setFormData(EMPTY);
    onOpenChange(isOpen);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/accounts`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${formData.business_name} created`);
      setFormData(EMPTY);
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create account");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Create advertiser account</DialogTitle>
          <DialogDescription>
            {/* POST /accounts creates the account and nothing else. Saying so here,
                because otherwise the business exists, looks complete on this page, and
                nobody can log into it. */}
            Creates the business record only. Nobody can sign in to it until you invite
            a user from the Users page.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="business_name">Business name</Label>
              <Input
                id="business_name"
                value={formData.business_name}
                onChange={(e) => setFormData((p) => ({ ...p, business_name: e.target.value }))}
                required
                data-testid="account-business-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="abn">ABN</Label>
              <Input
                id="abn"
                value={formData.abn}
                onChange={(e) => setFormData((p) => ({ ...p, abn: e.target.value }))}
                placeholder="XX XXX XXX XXX"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Tier</Label>
            <Select
              value={formData.tier}
              onValueChange={(value) => setFormData((p) => ({ ...p, tier: value }))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIERS.map((tier) => (
                  <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4 p-4 rounded-lg bg-secondary/20 border border-border">
            <h4 className="font-semibold">Primary contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ["name", "Contact name", "text", true, undefined],
                ["email", "Email", "email", true, undefined],
                ["phone", "Phone", "text", true, "+61 4XX XXX XXX"],
                ["role", "Role", "text", false, undefined],
              ].map(([field, label, type, required, placeholder]) => (
                <div key={field} className="space-y-2">
                  <Label>{label}</Label>
                  <Input
                    type={type}
                    value={formData.primary_contact[field]}
                    onChange={(e) => setContact(field, e.target.value)}
                    required={required}
                    placeholder={placeholder}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="save-account-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Create account
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
